import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { LogOut, Users, GraduationCap, BookOpen, CreditCard, Plus, Trash2, Edit, MessageSquare, Send, Filter, ClipboardList, CalendarClock, Award, Lock, Unlock, Video, Search } from 'lucide-react';
import { toast } from 'sonner';
import { Checkbox } from '../components/ui/checkbox';
import MessageFiles from '../components/MessageFiles';
import LessonFiles from '../components/LessonFiles';
import RichTextEditor from '../components/RichTextEditor';
import FilePickerInput from '../components/FilePickerInput';
import PasswordInput from '../components/PasswordInput';
import YouTubeEmbed from '../components/YouTubeEmbed';
import { NotificationBell } from '../components/NotificationBell';
import { ChangePasswordDialog } from '../components/ChangePasswordDialog';
import AttendancePage from '../components/AttendancePage';
import NewSchoolYearWizard from '../components/NewSchoolYearWizard';
import CompetenciesPage from '../components/CompetenciesPage';
import { OnlineLessonsPanel } from '../components/online/OnlineLessonsPanel';
import StudentsTab from './admin/StudentsTab';
import TeachersTab from './admin/TeachersTab';
import ClassesTab from './admin/ClassesTab';
import LessonsTab from './admin/LessonsTab';
import MessagesTab from './admin/MessagesTab';
import PaymentsTab from './admin/PaymentsTab';
import DirectDebitTab from './admin/DirectDebitTab';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getFullName = (user) => {
  if (!user) return '';
  const full = `${user.first_name || ''} ${user.last_name || ''}`.trim();
  return full || user.username || '';
};

export default function AdminDashboard() {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [classes, setClasses] = useState([]);
  const [boardMessages, setBoardMessages] = useState([]);
  const [privateMessages, setPrivateMessages] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading] = useState(true);

  // User form
  const [newUser, setNewUser] = useState({
    username: '',
    first_name: '',
    last_name: '',
    password: '',
    email: '',
    role: 'student',
    class_ids: []
  });

  // Edit user form
  const [editUser, setEditUser] = useState(null);

  // Class form
  const [newClassName, setNewClassName] = useState('');

  // Payment form
  const [paymentStudent, setPaymentStudent] = useState('');
  const [paymentMonth, setPaymentMonth] = useState(1);
  const [paymentYear, setPaymentYear] = useState(new Date().getFullYear());
  const [paymentStatus, setPaymentStatus] = useState('paid');

  // Board message form
  const [boardContent, setBoardContent] = useState('');
  const [boardClassId, setBoardClassId] = useState('');
  const [boardFiles, setBoardFiles] = useState([]);

  // Private message form
  const [privateContent, setPrivateContent] = useState('');
  const [privateRecipient, setPrivateRecipient] = useState('');
  const [privateFiles, setPrivateFiles] = useState([]);

  // Filters
  const [boardFilter, setBoardFilter] = useState('all');
  const [privateFilter, setPrivateFilter] = useState('all');
  const [messageSearch, setMessageSearch] = useState('');

  // Lessons state
  const [lessons, setLessons] = useState([]);
  const [lessonClassFilter, setLessonClassFilter] = useState('all');

  // Edit lesson state (admin)
  const [editLessonOpen, setEditLessonOpen] = useState(false);
  const [editingLesson, setEditingLesson] = useState(null);
  const [editLessonDate, setEditLessonDate] = useState('');
  const [editLessonDescription, setEditLessonDescription] = useState('');
  const [editLessonNewFiles, setEditLessonNewFiles] = useState([]);
  const [editLessonFilesToRemove, setEditLessonFilesToRemove] = useState(new Set());

  // Edit message state
  const [editingMessage, setEditingMessage] = useState(null);
  const [editMessageOpen, setEditMessageOpen] = useState(false);
  const [editMessageContent, setEditMessageContent] = useState('');
  const [editMessageFilesToRemove, setEditMessageFilesToRemove] = useState(new Set());
  const [editMessageNewFiles, setEditMessageNewFiles] = useState([]);

  // Payments state
  const [payments, setPayments] = useState([]);

  // Dialog states
  const [createStudentDialogOpen, setCreateStudentDialogOpen] = useState(false);
  const [adminActiveTab, setAdminActiveTab] = useState('students');
  const [studentSearch, setStudentSearch] = useState('');
  const [teacherSearch, setTeacherSearch] = useState('');
  const [createTeacherDialogOpen, setCreateTeacherDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [classDialogOpen, setClassDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [newYearWizardOpen, setNewYearWizardOpen] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };

      const [usersRes, classesRes, messagesRes, recipientsRes, lessonsRes, paymentsRes] = await Promise.all([
        axios.get(`${API_URL}/users`, { headers }),
        axios.get(`${API_URL}/classes`, { headers }),
        axios.get(`${API_URL}/messages`, { headers }),
        axios.get(`${API_URL}/messages/recipients`, { headers }),
        axios.get(`${API_URL}/lessons`, { headers }),
        axios.get(`${API_URL}/payments`, { headers })
      ]);

      setUsers(usersRes.data);
      setClasses(classesRes.data);
      
      const allMessages = messagesRes.data;
      setBoardMessages(allMessages.filter(m => m.message_type === 'board'));
      setPrivateMessages(allMessages.filter(m => m.message_type === 'individual'));
      
      setRecipients(recipientsRes.data);
      setLessons(lessonsRes.data);
      setPayments(paymentsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Грешка при зареждане на данните');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreateUser = async (e) => {
    e.preventDefault();

    try {
      await axios.post(`${API_URL}/users`, newUser, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Потребителят е създаден успешно');
      setCreateStudentDialogOpen(false);
      setCreateTeacherDialogOpen(false);
      setNewUser({
        username: '',
        first_name: '',
        last_name: '',
        password: '',
        email: '',
        role: 'student',
        class_ids: []
      });
      fetchData();
    } catch (error) {
      console.error('Failed to create user:', error);
      toast.error(error.response?.data?.detail || 'Грешка при създаване на потребител');
    }
  };

  const handleOpenCreateStudent = () => {
    setNewUser({
      username: '',
      first_name: '',
      last_name: '',
      password: '',
      email: '',
      role: 'student',
      class_ids: [],
      mon_listed: false,
      parent_email: ''
    });
    setCreateStudentDialogOpen(true);
  };

  const handleOpenCreateTeacher = () => {
    setNewUser({
      username: '',
      first_name: '',
      last_name: '',
      password: '',
      email: '',
      role: 'teacher',
      class_ids: []
    });
    setCreateTeacherDialogOpen(true);
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Сигурни ли сте, че искате да изтриете този потребител?')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Потребителят е изтрит успешно');
      fetchData();
    } catch (error) {
      console.error('Failed to delete user:', error);
      toast.error('Грешка при изтриване на потребител');
    }
  };

  const handleUnlockUser = async (userId, username) => {
    try {
      await axios.post(`${API_URL}/users/${userId}/unlock`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Акаунтът на ${username} е отключен`);
      fetchData();
    } catch (error) {
      console.error('Failed to unlock user:', error);
      toast.error(error.response?.data?.detail || 'Грешка при отключване');
    }
  };

  const handleEditUser = (userToEdit) => {
    setEditUser({
      user_id: userToEdit.user_id,
      username: userToEdit.username,
      first_name: userToEdit.first_name || '',
      last_name: userToEdit.last_name || '',
      email: userToEdit.email || '',
      password: '',
      role: userToEdit.role,
      class_ids: userToEdit.class_ids || [],
      mon_listed: userToEdit.mon_listed || false,
      parent_email: userToEdit.parent_email || ''
    });
    setEditDialogOpen(true);
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();

    try {
      const updatePayload = {
        username: editUser.username,
        first_name: editUser.first_name,
        last_name: editUser.last_name,
        email: editUser.email,
        role: editUser.role,
        class_ids: editUser.class_ids,
        mon_listed: editUser.mon_listed,
        parent_email: editUser.parent_email
      };

      if (editUser.password) {
        updatePayload.password = editUser.password;
      }

      await axios.put(`${API_URL}/users/${editUser.user_id}`, updatePayload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Потребителят е обновен успешно');
      setEditDialogOpen(false);
      setEditUser(null);
      fetchData();
    } catch (error) {
      console.error('Failed to update user:', error);
      toast.error(error.response?.data?.detail || 'Грешка при обновяване на потребител');
    }
  };

  const toggleClassSelection = (classId, isEdit = false) => {
    if (isEdit) {
      setEditUser(prev => {
        const currentIds = prev.class_ids || [];
        if (currentIds.includes(classId)) {
          return { ...prev, class_ids: currentIds.filter(id => id !== classId) };
        } else {
          return { ...prev, class_ids: [...currentIds, classId] };
        }
      });
    } else {
      setNewUser(prev => {
        const currentIds = prev.class_ids || [];
        if (currentIds.includes(classId)) {
          return { ...prev, class_ids: currentIds.filter(id => id !== classId) };
        } else {
          return { ...prev, class_ids: [...currentIds, classId] };
        }
      });
    }
  };

  const handleSendBoardMessage = async () => {
    if (!boardContent.trim()) {
      toast.error('Моля, въведете съобщение');
      return;
    }

    if (!boardClassId) {
      toast.error('Моля, изберете клас');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('content', boardContent);
      formData.append('message_type', 'board');
      formData.append('class_id', boardClassId);
      
      if (boardFiles && boardFiles.length > 0) {
        Array.from(boardFiles).forEach(file => {
          formData.append('file', file);
        });
      }

      await axios.post(`${API_URL}/messages`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
        }
      });

      toast.success('Съобщението е изпратено');
      setBoardContent('');
      setBoardFiles([]);
      fetchData();
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error('Грешка при изпращане на съобщението');
    }
  };

  const handleSendPrivateMessage = async () => {
    if (!privateContent.trim()) {
      toast.error('Моля, въведете съобщение');
      return;
    }

    if (!privateRecipient) {
      toast.error('Моля, изберете получател');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('content', privateContent);
      formData.append('message_type', 'individual');
      formData.append('recipient_id', privateRecipient);
      
      if (privateFiles && privateFiles.length > 0) {
        Array.from(privateFiles).forEach(file => {
          formData.append('file', file);
        });
      }

      await axios.post(`${API_URL}/messages`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
        }
      });

      toast.success('Съобщението е изпратено');
      setPrivateContent('');
      setPrivateRecipient('');
      setPrivateFiles([]);
      fetchData();
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error('Грешка при изпращане на съобщението');
    }
  };

  // handleDownloadMessageFile is now handled by MessageFiles component

  const handleEditLesson = (lesson) => {
    setEditingLesson(lesson);
    setEditLessonDate(lesson.date);
    setEditLessonDescription(lesson.description);
    setEditLessonNewFiles([]);
    setEditLessonFilesToRemove(new Set());
    setEditLessonOpen(true);
  };

  const handleUpdateLesson = async (e) => {
    e.preventDefault();
    if (!editLessonDate || !editLessonDescription) {
      toast.error('Моля попълнете датата и описанието');
      return;
    }
    try {
      const formData = new FormData();
      formData.append('date', editLessonDate);
      formData.append('description', editLessonDescription);
      formData.append('remove_file_indices', Array.from(editLessonFilesToRemove).join(','));
      if (editLessonNewFiles.length > 0) {
        editLessonNewFiles.forEach(f => formData.append('file', f));
      }
      await axios.put(`${API_URL}/lessons/${editingLesson.lesson_id}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Записът е обновен успешно');
      setEditLessonOpen(false);
      setEditingLesson(null);
      fetchData();
    } catch (error) {
      toast.error('Грешка при обновяване на записа');
    }
  };

  const handleDeleteLesson = async (lessonId) => {
    if (!window.confirm('Сигурни ли сте, че искате да изтриете този учебен запис?')) {
      return;
    }
    try {
      await axios.delete(`${API_URL}/lessons/${lessonId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Учебният запис е изтрит успешно');
      fetchData();
    } catch (error) {
      console.error('Failed to delete lesson:', error);
      toast.error(error.response?.data?.detail || 'Грешка при изтриване на записа');
    }
  };

  const handleDeleteMessage = async (messageId) => {
    if (!window.confirm('Сигурни ли сте, че искате да изтриете това съобщение?')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/messages/${messageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Съобщението е изтрито успешно');
      fetchData();
    } catch (error) {
      console.error('Failed to delete message:', error);
      toast.error(error.response?.data?.detail || 'Грешка при изтриване на съобщението');
    }
  };

  const handleEditMessage = (msg) => {
    setEditingMessage(msg);
    setEditMessageContent(msg.content || '');
    setEditMessageFilesToRemove(new Set());
    setEditMessageNewFiles([]);
    setEditMessageOpen(true);
  };

  const handleUpdateMessage = async (e) => {
    e.preventDefault();
    if (!editMessageContent.trim()) { toast.error('Съобщението не може да е празно'); return; }
    try {
      const formData = new FormData();
      formData.append('content', editMessageContent);
      formData.append('remove_file_indices', Array.from(editMessageFilesToRemove).join(','));
      editMessageNewFiles.forEach(f => formData.append('file', f));
      await axios.put(`${API_URL}/messages/${editingMessage.message_id}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Съобщението е обновено успешно');
      setEditMessageOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Грешка при обновяване на съобщението');
    }
  };

  const getSenderRoleColor = (role) => {
    if (role === 'teacher') return 'bg-blue-100 text-blue-700';
    if (role === 'student') return 'bg-green-100 text-green-700';
    if (role === 'administrator') return 'bg-purple-100 text-purple-700';
    return 'bg-slate-100 text-slate-700';
  };

  const isImageFile = (filePath) => {
    if (!filePath) return false;
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.svg'];
    return imageExtensions.some(ext => filePath.toLowerCase().endsWith(ext));
  };

  const handleCreateClass = async (e) => {
    e.preventDefault();

    try {
      await axios.post(
        `${API_URL}/classes`,
        { name: newClassName, teacher_ids: [] },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Класът е създаден успешно');
      setClassDialogOpen(false);
      setNewClassName('');
      fetchData();
    } catch (error) {
      console.error('Failed to create class:', error);
      toast.error('Грешка при създаване на клас');
    }
  };

  const handleDeleteClass = async (classId) => {
    if (!window.confirm('Сигурни ли сте, че искате да изтриете този клас?')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/classes/${classId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Класът е изтрит успешно');
      fetchData();
    } catch (error) {
      console.error('Failed to delete class:', error);
      toast.error('Грешка при изтриване на клас');
    }
  };

  const handleUpdatePayment = async (e) => {
    e.preventDefault();

    try {
      await axios.post(
        `${API_URL}/payments`,
        {
          student_id: paymentStudent,
          month: parseInt(paymentMonth),
          year: parseInt(paymentYear),
          status: paymentStatus
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Статусът на плащането е обновен');
      setPaymentDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Failed to update payment:', error);
      toast.error('Грешка при обновяване на плащането');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getRoleLabel = (role) => {
    const labels = {
      student: 'Ученик',
      teacher: 'Учител',
      administrator: 'Администратор'
    };
    return labels[role] || role;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Зареждане...</div>
      </div>
    );
  }

  const students = users.filter(u => u.role === 'student');
  const onlineMode = user?.role === 'online_administrator';
  const teachers = users.filter(u => u.role === 'teacher');

  const matchUser = (u, q) => {
    if (!q) return true;
    const needle = q.toLowerCase().trim();
    const full = `${u.first_name || ''} ${u.last_name || ''} ${u.username || ''} ${u.email || ''}`.toLowerCase();
    const classNames = (u.class_ids || []).map(cid => (classes.find(c => c.class_id === cid)?.name || '')).join(' ').toLowerCase();
    return full.includes(needle) || classNames.includes(needle);
  };
  const filteredStudents = students.filter(s => matchUser(s, studentSearch));
  const filteredTeachers = teachers.filter(t => matchUser(t, teacherSearch));

  const ctx = {
    token,
    onlineMode,
    classes,
    users,
    students,
    teachers,
    filteredStudents,
    filteredTeachers,
    newUser,
    setNewUser,
    createStudentDialogOpen,
    setCreateStudentDialogOpen,
    createTeacherDialogOpen,
    setCreateTeacherDialogOpen,
    handleOpenCreateStudent,
    handleOpenCreateTeacher,
    handleCreateUser,
    handleEditUser,
    handleDeleteUser,
    handleUnlockUser,
    toggleClassSelection,
    studentSearch,
    setStudentSearch,
    teacherSearch,
    setTeacherSearch,
    newClassName,
    setNewClassName,
    classDialogOpen,
    setClassDialogOpen,
    handleCreateClass,
    handleDeleteClass,
    setNewYearWizardOpen,
    lessons,
    lessonClassFilter,
    setLessonClassFilter,
    handleEditLesson,
    handleDeleteLesson,
    boardContent,
    setBoardContent,
    boardClassId,
    setBoardClassId,
    boardFiles,
    setBoardFiles,
    handleSendBoardMessage,
    privateContent,
    setPrivateContent,
    privateRecipient,
    setPrivateRecipient,
    privateFiles,
    setPrivateFiles,
    handleSendPrivateMessage,
    recipients,
    boardMessages,
    privateMessages,
    boardFilter,
    setBoardFilter,
    privateFilter,
    setPrivateFilter,
    messageSearch,
    setMessageSearch,
    getSenderRoleColor,
    handleEditMessage,
    handleDeleteMessage,
    payments,
    paymentDialogOpen,
    setPaymentDialogOpen,
    handleUpdatePayment,
    paymentStudent,
    setPaymentStudent,
    paymentMonth,
    setPaymentMonth,
    paymentYear,
    setPaymentYear,
    paymentStatus,
    setPaymentStatus,
  };

  return (
    <div className="min-h-screen" style={{backgroundColor: '#F8EDE2'}}>
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-3 sm:py-4">
          {/* Desktop header */}
          <div className="hidden sm:flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div>
                <h1 className="text-2xl font-bold text-slate-900">БНУ Азбукарче</h1>
                <p className="text-sm text-slate-600">Администратор: {user.username}</p>
                {onlineMode && (
                  <span data-testid="online-admin-badge" className="inline-block mt-1 text-xs font-medium bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full">
                    Онлайн обучение
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <NotificationBell token={token} />
              <ChangePasswordDialog token={token} />
              <Button
                onClick={handleLogout}
                variant="outline"
                data-testid="logout-btn"
                className="flex items-center gap-2"
              >
                <LogOut className="h-4 w-4" />
                Изход
              </Button>
            </div>
          </div>

          {/* Mobile header */}
          <div className="flex sm:hidden items-center justify-between">
            <div className="flex items-center gap-2">
              <div>
                <h1 className="text-lg font-bold text-slate-900">БНУ Азбукарче</h1>
                <p className="text-xs text-slate-600">{user.username}</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <NotificationBell token={token} />
              <ChangePasswordDialog token={token} />
              <Button
                onClick={handleLogout}
                variant="outline"
                size="sm"
                data-testid="logout-btn"
                className="flex items-center gap-1"
              >
                <LogOut className="h-3 w-3" />
                <span className="text-xs">Изход</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8">
        <Tabs defaultValue="students" className="space-y-6" onValueChange={setAdminActiveTab}>
          {/* Mobile: 2 rows x 4 columns, Desktop: 1 row x 8 columns */}
          <TabsList data-testid="admin-tabs" className="flex flex-wrap w-full h-auto gap-1 p-1 bg-slate-100 rounded-lg">
            <TabsTrigger value="students" data-testid="students-tab" className="flex-1 min-w-[calc(25%-4px)] sm:min-w-0 flex-col sm:flex-row gap-0.5 sm:gap-1 py-2 px-1 sm:px-3 text-[10px] sm:text-sm">
              <GraduationCap className="h-4 w-4" />
              <span>Ученици</span>
            </TabsTrigger>
            <TabsTrigger value="teachers" data-testid="teachers-tab" className="flex-1 min-w-[calc(25%-4px)] sm:min-w-0 flex-col sm:flex-row gap-0.5 sm:gap-1 py-2 px-1 sm:px-3 text-[10px] sm:text-sm">
              <BookOpen className="h-4 w-4" />
              <span>Учители</span>
            </TabsTrigger>
            <TabsTrigger value="classes" data-testid="classes-tab" className="flex-1 min-w-[calc(25%-4px)] sm:min-w-0 flex-col sm:flex-row gap-0.5 sm:gap-1 py-2 px-1 sm:px-3 text-[10px] sm:text-sm">
              <Users className="h-4 w-4" />
              <span>Класове</span>
            </TabsTrigger>
            <TabsTrigger value="lessons" data-testid="lessons-tab" className="flex-1 min-w-[calc(25%-4px)] sm:min-w-0 flex-col sm:flex-row gap-0.5 sm:gap-1 py-2 px-1 sm:px-3 text-[10px] sm:text-sm">
              <BookOpen className="h-4 w-4" />
              <span>Записи</span>
            </TabsTrigger>
            <TabsTrigger value="messages" data-testid="messages-tab" className="flex-1 min-w-[calc(25%-4px)] sm:min-w-0 flex-col sm:flex-row gap-0.5 sm:gap-1 py-2 px-1 sm:px-3 text-[10px] sm:text-sm">
              <MessageSquare className="h-4 w-4" />
              <span>Съобщ.</span>
            </TabsTrigger>
            <TabsTrigger value="payments" data-testid="payments-tab" className="flex-1 min-w-[calc(25%-4px)] sm:min-w-0 flex-col sm:flex-row gap-0.5 sm:gap-1 py-2 px-1 sm:px-3 text-[10px] sm:text-sm">
              <CreditCard className="h-4 w-4" />
              <span>Плащ.</span>
            </TabsTrigger>
            <TabsTrigger value="attendance" data-testid="attendance-tab" className="flex-1 min-w-[calc(25%-4px)] sm:min-w-0 flex-col sm:flex-row gap-0.5 sm:gap-1 py-2 px-1 sm:px-3 text-[10px] sm:text-sm">
              <ClipboardList className="h-4 w-4" />
              <span>Присъст.</span>
            </TabsTrigger>
            <TabsTrigger value="competencies" data-testid="competencies-tab" className="flex-1 min-w-[calc(25%-4px)] sm:min-w-0 flex-col sm:flex-row gap-0.5 sm:gap-1 py-2 px-1 sm:px-3 text-[10px] sm:text-sm">
              <Award className="h-4 w-4" />
              <span>Комп.</span>
            </TabsTrigger>
            <TabsTrigger value="online" data-testid="online-tab" className="flex-1 min-w-[calc(25%-4px)] sm:min-w-0 flex-col sm:flex-row gap-0.5 sm:gap-1 py-2 px-1 sm:px-3 text-[10px] sm:text-sm">
              <Video className="h-4 w-4" />
              <span>Онлайн</span>
            </TabsTrigger>
            {onlineMode && (
              <TabsTrigger value="directdebit" data-testid="directdebit-tab" className="flex-1 min-w-[calc(25%-4px)] sm:min-w-0 flex-col sm:flex-row gap-0.5 sm:gap-1 py-2 px-1 sm:px-3 text-[10px] sm:text-sm">
                <CreditCard className="h-4 w-4" />
                <span>Дир. дебит</span>
              </TabsTrigger>
            )}
          </TabsList>

          {/* Edit User Dialog - Global for all tabs */}
          <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Редактиране на потребител</DialogTitle>
              </DialogHeader>
              {editUser && (
                <form onSubmit={handleUpdateUser} className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="edit-first-name">Име</Label>
                      <Input
                        id="edit-first-name"
                        value={editUser.first_name}
                        onChange={(e) => setEditUser({ ...editUser, first_name: e.target.value })}
                        placeholder="Иван"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="edit-last-name">Фамилия</Label>
                      <Input
                        id="edit-last-name"
                        value={editUser.last_name}
                        onChange={(e) => setEditUser({ ...editUser, last_name: e.target.value })}
                        placeholder="Иванов"
                        className="mt-1"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="edit-username">Потребителско име (за вход)</Label>
                    <Input
                      id="edit-username"
                      value={editUser.username}
                      onChange={(e) => setEditUser({ ...editUser, username: e.target.value })}
                      required
                      className="mt-1"
                    />
                  </div>
                  {editUser.password_note && (
                    <div>
                      <Label className="text-slate-600 flex items-center gap-1.5">
                        Текуща парола
                        <span className="text-xs text-slate-400 font-normal">(само за администратора)</span>
                      </Label>
                      <PasswordInput
                        value={editUser.password_note}
                        readOnly
                        className="mt-1 text-slate-600"
                      />
                    </div>
                  )}
                  <div>
                    <Label htmlFor="edit-password">Нова парола <span className="text-slate-400 font-normal text-xs">(оставете празно, ако не искате да променяте)</span></Label>
                    <PasswordInput
                      id="edit-password"
                      value={editUser.password}
                      onChange={(e) => setEditUser({ ...editUser, password: e.target.value })}
                      placeholder="Въведете нова парола"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-email">Имейл</Label>
                    <Input
                      id="edit-email"
                      type="email"
                      value={editUser.email}
                      onChange={(e) => setEditUser({ ...editUser, email: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Класове (можете да изберете повече от един)</Label>
                    <div className="mt-2 space-y-2 max-h-40 overflow-y-auto border border-slate-200 rounded-md p-3">
                      {classes.map((cls) => (
                        <div key={cls.class_id} className="flex items-center space-x-2">
                          <Checkbox
                            id={`edit-class-${cls.class_id}`}
                            checked={editUser.class_ids.includes(cls.class_id)}
                            onCheckedChange={() => toggleClassSelection(cls.class_id, true)}
                          />
                          <label
                            htmlFor={`edit-class-${cls.class_id}`}
                            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                          >
                            {cls.name}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                  {onlineMode && editUser.role === 'student' && (
                    <div className="flex items-center space-x-2 rounded-md border border-indigo-200 bg-indigo-50 p-3">
                      <Checkbox
                        id="edit-mon-listed"
                        data-testid="edit-mon-listed-checkbox"
                        checked={!!editUser.mon_listed}
                        onCheckedChange={(v) => setEditUser({ ...editUser, mon_listed: !!v })}
                      />
                      <label htmlFor="edit-mon-listed" className="text-sm font-medium text-indigo-800 cursor-pointer">
                        В списъка на МОН
                      </label>
                    </div>
                  )}
                  {onlineMode && editUser.role === 'student' && (
                    <div>
                      <Label htmlFor="edit-parent-email">Имейл на родител (за директен дебит)</Label>
                      <Input
                        id="edit-parent-email"
                        data-testid="edit-parent-email-input"
                        type="email"
                        value={editUser.parent_email || ''}
                        onChange={(e) => setEditUser({ ...editUser, parent_email: e.target.value })}
                        placeholder="parent@example.com"
                      />
                    </div>
                  )}
                  <Button type="submit" className="w-full bg-[#00966E] hover:bg-[#007a59] text-white">
                    Обнови
                  </Button>
                </form>
              )}
            </DialogContent>
          </Dialog>

          <StudentsTab ctx={ctx} />

          <TeachersTab ctx={ctx} />

          <ClassesTab ctx={ctx} />

          <LessonsTab ctx={ctx} />

          <MessagesTab ctx={ctx} />

          <PaymentsTab ctx={ctx} />

          <TabsContent value="attendance" data-testid="attendance-content">
            <AttendancePage classes={classes} token={token} />
          </TabsContent>

          <TabsContent value="competencies" data-testid="competencies-content">
            <CompetenciesPage classes={classes} token={token} />
          </TabsContent>

          <TabsContent value="online" data-testid="online-content">
            <OnlineLessonsPanel role="administrator" />
          </TabsContent>

          {onlineMode && (
            <TabsContent value="directdebit" data-testid="directdebit-content">
              <DirectDebitTab />
            </TabsContent>
          )}
        </Tabs>
      </main>

      {/* Edit Lesson Dialog */}
      {/* Message Edit Dialog */}
      <Dialog open={editMessageOpen} onOpenChange={setEditMessageOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Редактиране на съобщение</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateMessage} className="space-y-4">
            <div>
              <Label>Съдържание</Label>
              <div className="mt-1">
                <RichTextEditor
                  value={editMessageContent}
                  onChange={setEditMessageContent}
                  placeholder="Въведете съобщение..."
                />
              </div>
            </div>
            {editingMessage && (editingMessage.file_paths?.length > 0) && (
              <div>
                <Label className="text-slate-600 text-sm">Прикачени файлове</Label>
                <div className="mt-1 space-y-1.5">
                  {editingMessage.file_paths.map((fp, i) => {
                    const name = editingMessage.file_names?.[i] || 'файл';
                    const removed = editMessageFilesToRemove.has(i);
                    return (
                      <div key={fp || `file-${i}`} className={`flex items-center justify-between px-3 py-2 rounded-lg border text-sm ${removed ? 'bg-red-50 border-red-200 line-through text-slate-400' : 'bg-slate-50 border-slate-200'}`}>
                        <span className="truncate">{name}</span>
                        <button
                          type="button"
                          onClick={() => setEditMessageFilesToRemove(prev => {
                            const next = new Set(prev);
                            if (next.has(i)) next.delete(i); else next.add(i);
                            return next;
                          })}
                          className={`ml-2 text-xs flex-shrink-0 px-2 py-0.5 rounded ${removed ? 'text-green-600 hover:bg-green-100' : 'text-red-500 hover:bg-red-100'}`}
                        >{removed ? 'Върни' : 'Премахни'}</button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            <div>
              <Label className="mb-2 block">Добави нови файлове</Label>
              <FilePickerInput files={editMessageNewFiles} onChange={setEditMessageNewFiles} label="Прикачи файлове" />
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setEditMessageOpen(false)}>Отказ</Button>
              <Button type="submit" className="flex-1 bg-[#00966E] hover:bg-[#007a59] text-white">Запази</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={editLessonOpen} onOpenChange={setEditLessonOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Редактиране на запис</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateLesson} className="space-y-4">
            <div>
              <Label htmlFor="admin-edit-lesson-date">Дата</Label>
              <Input
                id="admin-edit-lesson-date"
                type="date"
                value={editLessonDate}
                onChange={(e) => setEditLessonDate(e.target.value)}
                required
                className="mt-1"
              />
            </div>
            <div>
              <Label>Описание на урока</Label>
              <div className="mt-1">
                <RichTextEditor
                  value={editLessonDescription}
                  onChange={setEditLessonDescription}
                  placeholder="Опишете съдържанието на урока..."
                />
              </div>
            </div>
            {editingLesson && (editingLesson.file_paths?.length > 0 || editingLesson.file_path) && (
              <div>
                <Label className="text-slate-600 text-sm">Съществуващи файлове</Label>
                <div className="mt-1 space-y-1.5">
                  {(editingLesson.file_paths?.length > 0 ? editingLesson.file_paths : [editingLesson.file_path]).map((fp, i) => {
                    const name = editingLesson.file_names?.[i] || editingLesson.file_name || 'файл';
                    const removed = editLessonFilesToRemove.has(i);
                    return (
                      <div key={fp || `file-${i}`} className={`flex items-center justify-between px-3 py-2 rounded-lg border text-sm ${removed ? 'bg-red-50 border-red-200 line-through text-slate-400' : 'bg-slate-50 border-slate-200'}`}>
                        <span className="truncate">{name}</span>
                        <button
                          type="button"
                          onClick={() => setEditLessonFilesToRemove(prev => {
                            const next = new Set(prev);
                            if (next.has(i)) next.delete(i); else next.add(i);
                            return next;
                          })}
                          className={`ml-2 text-xs flex-shrink-0 px-2 py-0.5 rounded ${removed ? 'text-green-600 hover:bg-green-100' : 'text-red-500 hover:bg-red-100'}`}
                        >{removed ? 'Върни' : 'Премахни'}</button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            <div>
              <Label className="mb-2 block">Добави нови файлове (опционално)</Label>
              <FilePickerInput
                files={editLessonNewFiles}
                onChange={setEditLessonNewFiles}
                label="Добавете файлове"
              />
            </div>
            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => setEditLessonOpen(false)}
              >
                Отказ
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-[#00966E] hover:bg-[#007a59] text-white"
              >
                Запази промените
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      {/* New School Year Wizard */}
      <NewSchoolYearWizard
        open={newYearWizardOpen}
        onClose={() => setNewYearWizardOpen(false)}
        token={token}
        onComplete={() => fetchData()}
      />

      {/* Floating Add Student Button */}
      {adminActiveTab === 'students' && (
        <button
          onClick={handleOpenCreateStudent}
          data-testid="fab-add-student-btn"
          className="fixed bottom-6 right-6 z-40 bg-[#00966E] hover:bg-[#007a59] text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-200 flex items-center gap-2 px-5 py-3 font-medium text-sm active:scale-95"
          style={{ boxShadow: '0 4px 20px rgba(0,150,110,0.4)' }}
        >
          <Plus className="h-5 w-5" />
          Добави ученик
        </button>
      )}
    </div>
  );
}
